import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, from, map, switchMap, throwError } from 'rxjs';
// import { environment } from '../../../environments/environment';
import { ApiOptions } from '../models/api-response.models';
import { ResponseUtilityService } from './response-utility.service';
import { SpinnerService } from './spinner.service';
import { API_CONFIG } from '../tokens/config.tokens';

@Injectable({
  providedIn: 'root'
})
export class ApiCallingService {
  baseUrl: any;
  #responseUtilityService = inject(ResponseUtilityService);
  #apiConfig = inject(API_CONFIG);

  constructor(
    private _httpClient: HttpClient,
    private _spinnerService: SpinnerService
  ) {
    this.baseUrl = this.#apiConfig.baseUrl;
  }

  getData<T>(
    controllerName: string,
    methodName: string,
    showLoader: boolean,
    options?: ApiOptions,
    showError: boolean = true
  ): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    return this._httpClient.get<any>(`${this.baseUrl}${controllerName}/${methodName}`, options).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => {
          return this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError);
        })
      )
    );
  }

  postData<T>(
    controllerName: string,
    methodName?: string,
    data?: any,
    showLoader?: boolean,
    options?: ApiOptions,
    showError: boolean = true
  ): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    if (methodName) {
      return this._httpClient
        .post<any>(`${this.baseUrl}${controllerName}/${methodName}`, data, options)
        .pipe(
          catchError((error: HttpErrorResponse) =>
            throwError(() =>
              this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
            )
          )
        );
    }
    return this._httpClient
      .post<any>(`${this.baseUrl}${controllerName}`, data, options)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  patchData<T>(
    controllerName: string,
    methodName: string,
    data: any,
    showLoader: boolean,
    options?: ApiOptions,
    showError: boolean = true
  ): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    return this._httpClient
      .patch<any>(`${this.baseUrl}${controllerName}/${methodName}`, data, options)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  fileUpload<T>(
    controllerName: string,
    methodName: string,
    data: any,
    showLoader: boolean,
    showError: boolean = true
  ): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    return this._httpClient
      .post<any>(`${this.baseUrl}${controllerName}/${methodName}`, data, {
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  awsFileUpload<T>(url: string, data: any, showLoader: boolean, showError: boolean = true): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    // Only include x-amz-acl header if the presigned URL was signed with it
    const signedHeadersParam = url.match(/X-Amz-SignedHeaders=([^&]+)/);
    const signedHeaders = signedHeadersParam ? decodeURIComponent(signedHeadersParam[1]).toLowerCase() : '';
    const requiresAclHeader = signedHeaders.includes('x-amz-acl');
    return this._httpClient
      .put<any>(url, data, {
        reportProgress: true,
        observe: 'events',
        headers: {
          // Required because presigned URL was created with x-amz-acl header for public uploads
          ...(requiresAclHeader ? { 'x-amz-acl': 'public-read' } : {}),
          ...(data?.type ? { 'Content-Type': data.type } : {})
        }
      })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  awsFileUploadWithFetch<T>(url: string, data: any, showLoader: boolean, showError: boolean = true): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }

    const signedHeadersParam = url.match(/X-Amz-SignedHeaders=([^&]+)/);
    const signedHeaders = signedHeadersParam ? decodeURIComponent(signedHeadersParam[1]).toLowerCase() : '';
    const requiresAclHeader = signedHeaders.includes('x-amz-acl');

    const headers: HeadersInit = {
      'Content-Type': data?.type ? data.type : 'application/octet-stream', // pass this in dynamically
      ...(requiresAclHeader ? { 'x-amz-acl': 'public-read' } : {})
    };

    return new Observable(observer => {
      fetch(url, {
        method: 'PUT',
        headers,
        body: data
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
          }
          observer.next(response);
          observer.complete();
        })
        .catch(error => {
          if (showError) {
            this.#responseUtilityService.handleResponse(error, 'Some error has occurred', showError, null);
          }
          observer.error(error);
        });
    });
  }

  putData<T>(
    controllerName: string,
    methodName?: string,
    data?: any,
    showLoader?: boolean,
    showError: boolean = true
  ): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    const API_URL = `${this.baseUrl}${controllerName}${methodName ? `/${methodName}` : ''}`;
    return this._httpClient
      .put<any>(API_URL, data)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  deleteData<T>(
    controllerName: string,
    methodName: string,
    showLoader: boolean,
    showError: boolean = true
  ): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    return this._httpClient
      .delete<any>(`${this.baseUrl}${controllerName}/${methodName}`)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  deleteDataWithBody<T>(controllerName: string, methodName: string, data: any, showLoader: boolean): Observable<any> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }
    return this._httpClient.delete<any>(`${this.baseUrl}${controllerName}/${methodName}`, { body: data });
  }

  downloadFile(
    controllerName: string,
    methodName: string,
    body: any,
    showLoader: boolean,
    filenameDownloadName?: string,
    showError: boolean = true
  ): Observable<void> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }

    return this._httpClient
      .post(`${this.baseUrl}${controllerName}/${methodName}`, body, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Create blob from response
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') || 'application/octet-stream'
          });

          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
            : 'download';

          // Create download link and trigger
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameDownloadName ? filenameDownloadName : filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this._spinnerService.handleSpinnerStop();
        }),
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  downloadReportFile(
    controllerName: string,
    methodName: string,
    body?: any,
    showLoader?: boolean,
    filenameDownloadName?: string,
    showError: boolean = true
  ): Observable<void> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }

    return this._httpClient
      .get(`${this.baseUrl}${controllerName}/${methodName}${body ? `?${body}` : ''}`, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Create blob from response
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') || 'application/octet-stream'
          });

          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
            : 'download';

          // Create download link and trigger
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameDownloadName ? filenameDownloadName : filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this._spinnerService.handleSpinnerStop();
        }),
        catchError((error: HttpErrorResponse) => {
          if (showLoader) {
            this._spinnerService.handleSpinnerStop();
          }
          const errBody$ =
            error.error instanceof Blob
              ? from(
                  (error.error as Blob).text().then(t => {
                    try {
                      return JSON.parse(t) as object;
                    } catch {
                      return { message: error.statusText || 'Download failed' };
                    }
                  })
                )
              : from(Promise.resolve(error.error ?? { message: error.statusText || 'Some error has occurred' }));
          return errBody$.pipe(
            switchMap(errBody =>
              throwError(() =>
                this.#responseUtilityService.handleResponse(
                  errBody as any,
                  (errBody as any)?.message || error.statusText || 'Some error has occurred',
                  showError,
                  null
                )
              )
            )
          );
        })
      );
  }

  downloadReportFileWithBody(
    controllerName: string,
    methodName: string,
    body: any,
    showLoader: boolean,
    filenameDownloadName?: string,
    showError: boolean = true
  ): Observable<void> {
    if (showLoader) {
      this._spinnerService.handleSpinnerStart();
    }

    return this._httpClient
      .post(`${this.baseUrl}${controllerName}/${methodName}`, body, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Create blob from response
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') || 'application/octet-stream'
          });

          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
            : 'download';

          // Create download link and trigger
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameDownloadName ? filenameDownloadName : filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this._spinnerService.handleSpinnerStop();
        }),
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.#responseUtilityService.handleResponse(error.error, 'Some error has occurred', showError, null)
          )
        )
      );
  }

  async getBlobFromFileUrl(fileUrl: string): Promise<string> {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
}
